import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import { supabase } from "./lib/supabaseClient";
import { AuthProvider, useAuth } from "./auth/AuthProvider";

import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Intake from "./pages/Intake";
import AboutUs from "./pages/AboutUs";

/* ================= INTRO ================= */

function IntroOverlay({ onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3800);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="intro-overlay">
      <div className="intro-content">
        <svg
          aria-hidden="true"
          className="wowIntroHeartSvg"
          style={{ overflow: "visible" }}
          viewBox="0 0 24 24"
          width="140"
          height="140"
        >
          <defs>
            <filter id="wowHaloBlur" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Green halo heart behind */}
          <g transform="translate(12 12) scale(2.20) translate(-12 -12)">
            <path
              d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
              fill="#22c55e"
              opacity="0.85"
              filter="url(#wowHaloBlur)"
            />
          </g>

          {/* Red heart front */}
          <path
            d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
            fill="#ff2d55"
          />
        </svg>
        <div className="intro-logo">
          W<span className="intro-globe">🌍</span>W<span>50/50</span>
        </div>
        <div className="intro-tagline">
          Is a world within its own universe
        </div>
      </div>
    </div>
  );
}

/* ================= ROUTING ================= */

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
}


export default function App() {
  const [showIntro, setShowIntro] = useState(
    () => !sessionStorage.getItem("wow_intro_seen")
  );

  const handleIntroDone = () => {
    sessionStorage.setItem("wow_intro_seen", "1");
    setShowIntro(false);
  };

  return (
    <AuthProvider supabase={supabase}>
      {showIntro && <IntroOverlay onDone={handleIntroDone} />}

      <Routes>
        <Route path="/" element={<Landing page="home" />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/about" element={<Navigate to="/about-us" replace />} />
        {/* Landing section routes (same page, different paths for deep-links) */}
        {[
          "/top",
          "/concept",
          "/tracks",
          "/veterans",
          "/workforce",
          "/economic-opportunity",
          "/federal-partners",
          "/security-compliance",
          "/community-impact",
          "/plans",
          "/marketplace",
          "/donations",
          "/donation-intake",
          "/contribution",
          "/fundraising",
          "/payments",
          "/calculator",
          "/events",
          "/outreach",
          "/volunteer",
          "/final-arrangements",
          "/legal-listings",
          "/partners",
          "/registration",
        ].map((p) => (
          <Route key={p} path={p} element={<Landing page="home" />} />
        ))}

        {/* Legacy early-access deep-link redirects to Registration */}
        <Route path="/early-access" element={<Navigate to="/registration" replace />} />


        <Route path="/intake" element={<ProtectedRoute><Intake /></ProtectedRoute>} />

        <Route path="/login" element={<Login />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

